import { useCallback } from "react";

import { saveAs } from "file-saver";
import { omit } from "lodash";
import Papa from "papaparse";

import { useUser } from "@components/user-context";
import { SecondaryButton } from "@components/v2";
import { useQueryMetadata } from "@components/v2/modals";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type FetchOrderSummaryGraph,
  type FetchOrderSummaryGraphVariables,
} from "@north-beam/nb-client-side";
import { dateFormat } from "@pages/orders/chart-legend";
import { FETCH_ORDER_SUMMARY_GRAPH } from "@pages/settings/queries";

import { useOrderGraphFilters } from "./hooks/use-orders-filters";

export function OrderChartExport() {
  const { dateRange, granularity, ...restOfFilters } = useOrderGraphFilters();
  const { user } = useUser();
  const { addQueryMetadata } = useQueryMetadata();

  const { data: graphResponse, loading } = useNorthbeamQuery<
    FetchOrderSummaryGraph,
    FetchOrderSummaryGraphVariables
  >(FETCH_ORDER_SUMMARY_GRAPH, {
    variables: {
      filterOptions: omit(restOfFilters, "compareDateRange"),
      dateRange,
      granularity,
    },
    onCompleted(data) {
      addQueryMetadata({
        context: "Orders",
        topic: "Orders Chart",
        metadata: data.me.orderSummaryGraph.queryMetadata,
      });
    },
  });

  const onExport = useCallback(() => {
    const data = graphResponse?.me.orderSummaryGraph.data;
    if (!data) {
      return;
    }

    const headers = ["Date", "Revenue", "Transactions"];
    const rows = data.map((point) => {
      const row = [
        dateFormat(point.datetime, granularity, user.timezone),
        point.orderRevenue,
        point.orderCount,
      ];
      return row;
    });
    const csvString = Papa.unparse([headers, ...rows]);

    const blob = new Blob([csvString], {
      type: "text/plain;charset=utf-8",
    });

    saveAs(blob, "orders-chart.csv");
  }, [granularity, graphResponse?.me.orderSummaryGraph.data, user.timezone]);

  return (
    <SecondaryButton className="w-[10rem]" onClick={onExport} loading={loading}>
      EXPORT CHART DATA
    </SecondaryButton>
  );
}
